import React, { useEffect, useRef, useState, useMemo, useCallback, useContext } from 'react';
import { Box, Button, Drop, Text, Layer } from 'grommet';
import { CircleQuestion, FormEdit, FormTrash } from 'grommet-icons';
import { useRouteMatch } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import RichTextEditor from 'granite-admin/core/components/RichTextEditor';
import helpTextAPI from 'granite-admin/common/gateways/help-text-api';
import * as OrganisationDucks from 'granite-admin/organisations/ducks/organisations';
import * as UserDucks from 'granite-admin/accounts/ducks/user';
import * as HelpTextDucks from 'granite-admin/common/ducks/helptext';
import * as PanelPermissionsDucks from 'granite-admin/common/ducks/panelPermissions';
import Divider from 'granite-admin/core/components/Divider';
import { ConfigContext } from 'granite-admin/core/components/ConfigProvider';
import Tooltip from 'granite-admin/core/components/Tooltip';
import SaveIcon from 'granite-admin/common/assets/SaveIcon';
import CloseIcon from 'granite-admin/common/assets/CloseIcon';
const StyledBox = styled(Box)`
  visibility: ${props => props.active ? 'visible !important' : 'hidden'};
`;
const StyledEdit = styled(FormEdit)`
  &:hover {
    cursor: pointer;
    stroke: ${props => props.theme.global.colors['hover']};
  }
`;
const StyledTrash = styled(FormTrash)`
  &:hover {
    cursor: pointer;
    stroke: ${props => props.theme.global.colors['hover']};
  }
`;
const HelpTextForm = ({
  handleSubmit,
  value,
  errorMsg,
  setEditMode,
  setHelpText,
  showHelpTextAsSlider,
  val,
  handleChange,
  config
}) => {
  return /*#__PURE__*/React.createElement(Box, {
    width: showHelpTextAsSlider ? config?.sliderWidth : 'medium-l',
    pad: "small",
    gap: "small",
    flex: {
      shrink: 0
    }
  }, /*#__PURE__*/React.createElement(Box, {
    style: {
      overflowX: 'auto',
      position: 'sticky'
    }
  }, /*#__PURE__*/React.createElement(RichTextEditor, {
    placeholder: "Add field description",
    onChange: handleChange,
    initialValue: val,
    minHeight: showHelpTextAsSlider ? 800 : 300,
    maxHeight: showHelpTextAsSlider ? 800 : 300,
    buttons: "bold,italic,underline,strikethrough,ul,ol,fontsize,paragraph,superscript,subscript,image,video,table,link,left,right,center,justify,preview"
  })), /*#__PURE__*/React.createElement(Box, {
    direction: "row",
    gap: "small",
    justify: "between"
  }, /*#__PURE__*/React.createElement(Text, {
    as: "div",
    color: "status-critical"
  }, errorMsg), !showHelpTextAsSlider && /*#__PURE__*/React.createElement(Box, {
    direction: "row",
    gap: "small"
  }, /*#__PURE__*/React.createElement(Button, {
    primary: true,
    alignSelf: "end",
    label: "Cancel",
    size: "small",
    onClick: () => {
      setEditMode(false);
      setHelpText(false);
    }
  }), /*#__PURE__*/React.createElement(Button, {
    primary: true,
    alignSelf: "end",
    label: "Save",
    size: "small",
    onClick: () => {
      handleSubmit(val);
    },
    disabled: val === ''
  }))));
};
HelpTextForm.propTypes = {
  handleSubmit: PropTypes.func,
  value: PropTypes.any,
  errorMsg: PropTypes.string,
  setEditMode: PropTypes.func,
  setHelpText: PropTypes.func,
  showHelpTextAsSlider: PropTypes.bool
};
const HelpText = ({
  name,
  active,
  selectedOrganisation,
  userProfile,
  helptexts,
  dispatch,
  panelPermissions,
  showHelpTextAsSlider,
  ...rest
}) => {
  const config = useContext(ConfigContext);
  const {
    path: url,
    params
  } = useRouteMatch();
  const path = useMemo(() => {
    let theUrl = url;
    Object.keys(params).forEach(key => {
      if (key.includes('sr_')) theUrl = theUrl?.replace(`:${key}`, params[key]);
    });
    return theUrl;
  }, [url, params]);
  const haveHelpTextPermission = useMemo(() => panelPermissions[path]?.find(i => i.type === 'help_texts'), [panelPermissions, path]);
  const [content, setContent] = useState(() => {
    if (helptexts[path] && helptexts[path][name]) {
      return helptexts[path][name];
    }
  });
  const [showHelpText, setHelpText] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [val, setVal] = useState(content?.data || '');
  const handleChange = useCallback(data => setVal(data), []);
  const ref = useRef();
  useEffect(() => setVal(content?.data || ''), [content]);
  useEffect(() => {
    if (helptexts[path] && helptexts[path][name]) {
      setContent(helptexts[path][name]);
    }
  }, [helptexts, path, name]);
  if (!haveHelpTextPermission || !userProfile.isSystemAdmin && !content) {
    return null;
  }
  const toggleHelpText = () => {
    if (!content) setEditMode(true);
    setHelpText(prev => !prev);
    setErrorMsg(null);
  };
  const handleClose = e => {
    if (!showHelpTextAsSlider && (editMode || ref?.current?.contains(e.target))) return;
    setHelpText(false);
    setEditMode(false);
  };
  const handleSubmit = val => {
    const params = {
      control_id: name,
      context_id: path,
      help_text: val,
      organisation_id: Number(selectedOrganisation?.pk),
      extra_data: {},
      pk: content?.pk
    };
    setErrorMsg(null);
    helpTextAPI.createHelpText(params).then(res => {
      setContent({
        data: res.helpText,
        type: 'text',
        ...res
      });
      setEditMode(false);
    }).catch(err => {
      setErrorMsg(err?.errors?.title || 'Unable to save.');
    });
  };
  const handleEdit = () => {
    setEditMode(true);
  };
  const handleDelete = () => {
    helpTextAPI.deleteHelpText(content.pk).then(res => {
      const newHelpTexts = {
        ...helptexts
      };
      if (newHelpTexts[path]) delete newHelpTexts[path][name];
      dispatch(HelpTextDucks.updateHelpText(newHelpTexts));
      setContent(undefined);
      setEditMode(true);
    }).catch(err => {
      console.log(err);
    });
  };
  const renderContent = () => {
    let data;
    if (content && !editMode) {
      data = /*#__PURE__*/React.createElement(Box, {
        gap: "xsmall"
      }, !showHelpTextAsSlider && userProfile.isSystemAdmin && /*#__PURE__*/React.createElement(Box, {
        alignSelf: "end",
        direction: "row",
        gap: "small"
      }, /*#__PURE__*/React.createElement(StyledEdit, {
        size: "small",
        onClick: handleEdit
      }), /*#__PURE__*/React.createElement(StyledTrash, {
        size: "small",
        onClick: handleDelete
      })), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text, {
        color: content.type,
        dangerouslySetInnerHTML: {
          __html: content.data
        }
      }), ' '));
    } else if (editMode) {
      return userProfile.isSystemAdmin ? /*#__PURE__*/React.createElement(HelpTextForm, {
        handleSubmit: handleSubmit,
        value: content?.data,
        errorMsg: errorMsg,
        setEditMode: setEditMode,
        setHelpText: setHelpText,
        showHelpTextAsSlider: showHelpTextAsSlider,
        val: val,
        handleChange: handleChange,
        config: config
      }) : null;
    }
    return /*#__PURE__*/React.createElement(Box, {
      pad: "small",
      width: {
        max: 'large',
        min: 'medium'
      },
      height: {
        max: 'large'
      }
    }, data);
  };
  return /*#__PURE__*/React.createElement(StyledBox, {
    alignSelf: "center",
    style: {
      width: 'fit-content'
    },
    active: active || content,
    ref: ref
  }, /*#__PURE__*/React.createElement(Button, {
    alignSelf: "start",
    plain: true,
    focusIndicator: false,
    hoverIndicator: true,
    icon: config.helpText.icon ? config.helpText.icon : /*#__PURE__*/React.createElement(CircleQuestion, {
      size: "small"
    }),
    onClick: toggleHelpText
  }), ref.current && showHelpText && (showHelpTextAsSlider ? /*#__PURE__*/React.createElement(Layer, {
    position: "right",
    full: "vertical",
    onClickOutside: handleClose,
    onEsc: handleClose
  }, /*#__PURE__*/React.createElement(Box, {
    flex: true,
    width: config?.sliderWidth
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "row",
    justify: "end"
  }, /*#__PURE__*/React.createElement(Box, {
    align: "center",
    justify: "center",
    width: "25px",
    height: "25px",
    onClick: handleClose,
    background: "transparent",
    style: {
      position: 'absolute',
      left: '-12px',
      top: '0px',
      overflow: 'hidden',
      borderRadius: '50%'
    },
    focusIndicator: false
  }, /*#__PURE__*/React.createElement(CloseIcon, null)), content && !editMode && userProfile.isSystemAdmin && /*#__PURE__*/React.createElement(Box, {
    direction: "row",
    gap: "small"
  }, /*#__PURE__*/React.createElement(StyledEdit, {
    size: "medium",
    onClick: handleEdit
  }), /*#__PURE__*/React.createElement(StyledTrash, {
    size: "medium",
    onClick: handleDelete
  })), editMode && userProfile.isSystemAdmin && /*#__PURE__*/React.createElement(Button, {
    margin: {
      top: 'xsmall',
      right: 'xsmall'
    },
    label: /*#__PURE__*/React.createElement(Tooltip, {
      align: "bottom",
      content: "Save"
    }, /*#__PURE__*/React.createElement(SaveIcon, {
      size: "small"
    })),
    disabled: val === '',
    plain: true,
    onClick: () => {
      handleSubmit(val);
    }
  })), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(Box, {
    flex: true,
    overflow: "auto"
  }, renderContent()))) : /*#__PURE__*/React.createElement(Drop, {
    margin: {
      horizontal: 'xsmall'
    },
    stretch: false,
    align: {
      top: 'bottom',
      right: 'right'
    },
    target: ref.current,
    elevation: "small",
    onClickOutside: handleClose,
    restrictFocus: true
  }, renderContent())));
};
HelpText.defaultProps = {
  name: undefined
};
HelpText.propTypes = {
  name: PropTypes.string,
  active: PropTypes.bool,
  selectedOrganisation: PropTypes.object,
  userProfile: PropTypes.object,
  helptexts: PropTypes.object,
  dispatch: PropTypes.func,
  panelPermissions: PropTypes.object,
  showHelpTextAsSlider: PropTypes.bool
};
const mapStateToProps = state => ({
  userProfile: UserDucks.profile(state),
  selectedOrganisation: OrganisationDucks.selectedOrganisation(state),
  helptexts: HelpTextDucks.helptexts(state),
  panelPermissions: PanelPermissionsDucks.getPanelPermissions(state)
});
export default connect(mapStateToProps)(HelpText);